import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import {
  IconProps,
  DownloadIconProps,
  ContentfulIconProps,
  AngleDownIconProps,
  DropdownIconProps,
  DeltaProps,
} from './icon-atoms.types';

const DownloadIcon: React.FC<DownloadIconProps> = ({
  type,
  alt = '',
  width = '32',
  height = '32',
  fill = vars.coreDarkGrey,
  className,
}) => {
  switch (type) {
    case 'downloadIcon':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 31 31"
          fill={fill}
          aria-label={alt}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16 6.45378H11.4286V0H4.57143V6.45378H0L8 13.9832L16 6.45378ZM0 16.1345V18.2857H16V16.1345H0Z"
            fill={fill}
          />
        </svg>
      );
    case 'externalLinkIcon':
      return (
        <svg
          width={width}
          height={height}
          viewBox="0 0 52 52"
          fill={fill}
          aria-label={alt}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.7073 18.7281L12.7708 20.7917L20.0625 13.5L12.7708 6.20833L10.7073 8.27188L14.4771 12.0417H0.375V14.9583H14.4771L10.7073 18.7281ZM23.7083 0.375H3.29167C1.68021 0.375 0.375 1.68021 0.375 3.29167V9.125H3.29167V3.29167H23.7083V23.7083H3.29167V17.875H0.375V23.7083C0.375 25.3198 1.68021 26.625 3.29167 26.625H23.7083C25.3198 26.625 26.625 25.3198 26.625 23.7083V3.29167C26.625 1.68021 25.3198 0.375 23.7083 0.375Z"
            fill={fill}
          />
        </svg>
      );
    default:
      console.error(
        `DownloadIconAtom called with unrecognised prop "type": ${type}`
      );
      return null;
  }
};

export const DownloadIconAtom = styled(DownloadIcon)``;

const AngleDownIcon: React.FC<AngleDownIconProps> = ({
  className,
  fill = 'black',
  width = 25,
  height = 16,
  fillOpacity = 1,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 24 16"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
    fillOpacity={fillOpacity}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.46447 12L12 15.5355L15.5355 12L24 3.53553L20.4645 0L12 8.46447L3.53553 0L0 3.53553L8.46447 12Z"
      fill={fill}
    />
  </svg>
);

export const AngleDownIconAtom = styled(AngleDownIcon)``;

export const AngleUpIcon: React.FC<IconProps> = ({
  className,
  fill = 'black',
  width = 25,
  height = 16,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox="0 0 25 16"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5404 3.53638L12.0049 0.00084215L8.46935 3.53638L0.00487995 12.0008L3.54041 15.5364L12.0049 7.07191L20.4693 15.5364L24.0049 12.0008L15.5404 3.53638Z"
      fill={fill}
    />
  </svg>
);

export const AngleUpIconAtom = styled(AngleUpIcon)``;

const AngleLeftIcon: React.FC<IconProps> = ({
  className,
  fill = 'white',
  width = 9,
  height = 14,
  viewBox = '0 0 9 14',
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 1L1 7L8 13"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AngleLeftIconAtom = styled(AngleLeftIcon)``;

const AngleRightIcon: React.FC<IconProps> = ({
  className,
  width = 9,
  height = 14,
  viewBox = '0 0 9 14',
  fill = vars.coreDarkGrey,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 13L8 7L1 1"
      stroke={fill}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AngleRightIconAtom = styled(AngleRightIcon)``;

const ArrowRightIcon: React.FC<IconProps> = ({
  className,
  width = 17,
  height = 17,
  viewBox = '0 0 11 12',
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.91832 2.95834C7.75723 2.79725 7.49605 2.79725 7.33495 2.95834C7.17386 3.11943 7.17386 3.38061 7.33495 3.54171L9.38077 5.58752H0.549951C0.322134 5.58752 0.137451 5.77221 0.137451 6.00002C0.137451 6.22784 0.322134 6.41252 0.549951 6.41252H9.52744L7.33495 8.60501C7.17386 8.7661 7.17386 9.02728 7.33495 9.18837C7.49605 9.34946 7.75723 9.34946 7.91832 9.18837L10.7417 6.36504C10.9027 6.20395 10.9027 5.94277 10.7417 5.78168L7.91832 2.95834Z"
      fill="#2F2F46"
    />
  </svg>
);

export const ArrowRightIconAtom = styled(ArrowRightIcon)``;

const ArrowUpIcon: React.FC<IconProps> = ({
  className,
  width = 20,
  height = 20,
  viewBox = '0 0 20 20',
  fill = vars.coreDarkGrey,
  onClick,
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    onClick={onClick}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.9993 7.40317L3.28617 14.1134C2.97003 14.4294 2.45748 14.4294 2.14137 14.1133C1.82525 13.7973 1.82528 13.2849 2.14142 12.9689L9.08326 6.03008C9.08336 6.02998 9.08345 6.02989 9.08355 6.02979C9.0836 6.02974 9.08365 6.02969 9.08369 6.02965C9.32648 5.78683 9.65585 5.65039 9.9993 5.65039C10.3427 5.65039 10.6721 5.78683 10.9149 6.02965C10.9149 6.02969 10.915 6.02974 10.915 6.02979C10.9151 6.02989 10.9152 6.02998 10.9153 6.03008L17.8572 12.9689C18.1733 13.2849 18.1733 13.7973 17.8572 14.1133C17.5411 14.4294 17.0286 14.4294 16.7124 14.1134L9.9993 7.40317Z"
      fill={fill}
    />
  </svg>
);

export const ArrowUpIconAtom = styled(ArrowUpIcon)``;

const ArrowDownIcon: React.FC<IconProps> = ({
  className,
  width = 20,
  height = 20,
  viewBox = '0 0 20 20',
  fill = vars.coreDarkGrey,
  onClick = () => {},
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    onClick={onClick}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.23707 6.23704C2.55319 5.92101 3.06573 5.92098 3.38188 6.23699L10.095 12.9472L16.8081 6.23699C17.1243 5.92098 17.6368 5.92101 17.9529 6.23704C18.269 6.55308 18.269 7.06546 17.9529 7.38146L11.011 14.3203C11.011 14.3203 11.011 14.3203 11.011 14.3203C10.7682 14.5634 10.4386 14.7 10.095 14.7C9.75136 14.7 9.42181 14.5634 9.17899 14.3203C9.17898 14.3203 9.17897 14.3203 9.17896 14.3203L2.23712 7.38146C1.92098 7.06546 1.92096 6.55308 2.23707 6.23704Z"
      fill={fill}
    />
  </svg>
);

export const ArrowDownIconAtom = styled(ArrowDownIcon)``;

const ContentfulIcon: React.FC<ContentfulIconProps> = ({
  className = '',
  width = '24',
  height = '24',
  viewBox = '0 0 24 24',
}) => (
  <svg
    className={className}
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.79201 14.7664C9.42746 14.3993 9.13892 13.9638 8.94289 13.485C8.74686 13.0062 8.6472 12.4934 8.64961 11.976C8.64863 11.4588 8.75087 10.9466 8.95033 10.4694C9.14979 9.99213 9.44246 9.55951 9.8112 9.19681C10.0901 8.90469 10.2438 8.5152 10.2396 8.11136C10.2355 7.70753 10.0737 7.32129 9.78889 7.03499C9.50405 6.74869 9.11864 6.58497 8.71483 6.57874C8.31102 6.5725 7.92075 6.72424 7.6272 7.00161C6.30513 8.31754 5.55935 10.1044 5.55365 11.9697C5.54795 13.8351 6.28279 15.6264 7.5968 16.9504C7.7388 17.1007 7.90945 17.221 8.09868 17.3043C8.28792 17.3876 8.49191 17.4321 8.69863 17.4353C8.90536 17.4385 9.11063 17.4003 9.30234 17.3229C9.49406 17.2455 9.66834 17.1305 9.81491 16.9847C9.96147 16.8388 10.0774 16.6652 10.1557 16.4738C10.2341 16.2825 10.2734 16.0774 10.2713 15.8707C10.2691 15.664 10.2256 15.4598 10.1433 15.2701C10.061 15.0804 9.94155 14.9092 9.79201 14.7664Z"
      fill="#FFDA00"
    />
    <path
      d="M9.8112 9.1968C10.1783 8.83212 10.6137 8.54348 11.0925 8.34744C11.5714 8.1514 12.0842 8.05182 12.6016 8.0544C13.1188 8.05361 13.631 8.15593 14.1082 8.35537C14.5854 8.55482 15.018 8.8474 15.3808 9.216C15.5205 9.37314 15.6908 9.50015 15.8813 9.58927C16.0718 9.67839 16.2784 9.72774 16.4886 9.73432C16.6988 9.7409 16.9081 9.70457 17.1037 9.62754C17.2994 9.55051 17.4773 9.43441 17.6266 9.28631C17.7759 9.13822 17.8934 8.96125 17.972 8.76621C18.0507 8.57117 18.0887 8.36215 18.0838 8.15192C18.0789 7.9417 18.0312 7.73467 17.9436 7.54349C17.856 7.35232 17.7304 7.181 17.5744 7.04C16.9256 6.38298 16.1534 5.8606 15.3021 5.50284C14.4508 5.14508 13.5373 4.959 12.6139 4.95529C11.6905 4.95157 10.7755 5.13029 9.9214 5.48118C9.06728 5.83207 8.29089 6.34823 7.6368 7C7.37607 7.29432 7.23703 7.6768 7.24791 8.06985C7.25878 8.4629 7.41874 8.83711 7.69535 9.11657C7.97196 9.39602 8.3445 9.55981 8.73742 9.57471C9.13034 9.58961 9.51422 9.45451 9.8112 9.1968Z"
      fill="#67B3FF"
    />
    <path
      d="M15.3712 14.7856C15.004 15.1501 14.5686 15.4387 14.0898 15.6347C13.611 15.8307 13.0982 15.9304 12.5808 15.928C12.0636 15.9286 11.5515 15.8262 11.0743 15.6268C10.5972 15.4273 10.1645 15.1349 9.80159 14.7664C9.65819 14.6224 9.48782 14.508 9.30021 14.4298C9.11261 14.3516 8.91144 14.3111 8.70819 14.3107C8.50494 14.3102 8.30359 14.3498 8.11564 14.4272C7.9277 14.5046 7.75683 14.6182 7.61279 14.7616C7.46876 14.905 7.35438 15.0754 7.27619 15.263C7.198 15.4506 7.15752 15.6518 7.15708 15.855C7.15663 16.0583 7.19622 16.2596 7.27359 16.4476C7.35096 16.6355 7.46459 16.8064 7.60799 16.9504C8.25774 17.6076 9.03084 18.1301 9.88297 18.4878C10.7351 18.8456 11.6495 19.0316 12.5736 19.0351C13.4978 19.0387 14.4136 18.8598 15.2684 18.5086C16.1233 18.1575 16.9004 17.641 17.5552 16.9888C17.6999 16.8454 17.8149 16.6749 17.8936 16.4871C17.9724 16.2992 18.0134 16.0977 18.0143 15.894C18.0152 15.6903 17.976 15.4884 17.8988 15.2999C17.8217 15.1114 17.7082 14.9399 17.5648 14.7952C17.4214 14.6505 17.2509 14.5355 17.063 14.4568C16.8752 14.378 16.6737 14.337 16.47 14.3361C16.2663 14.3352 16.0644 14.3744 15.8759 14.4516C15.6874 14.5287 15.5159 14.6422 15.3712 14.7856Z"
      fill="#EB5A68"
    />
    <path
      d="M9.8112 9.19681C9.594 9.41335 9.3174 9.56053 9.01646 9.61969C8.71551 9.67885 8.40378 9.64733 8.12077 9.52912C7.83776 9.41092 7.59622 9.21134 7.42676 8.95571C7.25731 8.70007 7.16757 8.39987 7.16891 8.09317C7.17026 7.78647 7.26264 7.48707 7.43434 7.23294C7.60603 6.9788 7.84932 6.78136 8.13336 6.66564C8.4174 6.54993 8.72939 6.52115 9.0298 6.58295C9.33022 6.64476 9.60551 6.79436 9.8208 7.01281C10.1069 7.30487 10.2663 7.69794 10.2645 8.10678C10.2627 8.51561 10.0998 8.90726 9.8112 9.19681Z"
      fill="#47A1FF"
    />
    <path
      d="M9.78079 16.9504C9.56343 17.1668 9.28672 17.3138 8.98574 17.3727C8.68475 17.4316 8.37304 17.3999 8.09012 17.2815C7.8072 17.1631 7.5658 16.9633 7.39654 16.7076C7.22727 16.4518 7.13775 16.1515 7.13932 15.8448C7.14089 15.5381 7.23349 15.2388 7.40537 14.9848C7.57725 14.7308 7.82068 14.5335 8.1048 14.418C8.38893 14.3025 8.70094 14.2739 9.00131 14.336C9.30168 14.398 9.57686 14.5478 9.79199 14.7664C10.0783 15.0585 10.2377 15.4517 10.2356 15.8607C10.2335 16.2697 10.07 16.6613 9.78079 16.9504Z"
      fill="#D5465F"
    />
  </svg>
);

export const ContentfulIconAtom = styled(ContentfulIcon)``;

const DropdownIcon: React.FC<DropdownIconProps> = ({ isOpen, className }) => {
  return <div className={className} />;
};

export const DropdownIconAtom = styled(DropdownIcon)`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 6px 0 6px;
  border-color: ${vars.black} transparent transparent transparent;
  ${({ isOpen }) => (isOpen ? 'transform: rotate(180deg);' : null)}
  transition: transform ${vars.defaultTransition};
  margin-left: ${vars.spacingS};
`;

const Delta: React.FC<DeltaProps> = ({
  deltaValue,
  isValueDisplayed = true,
  className,
}) => {
  const displayValue =
    (deltaValue === 0 ? '= 0' : deltaValue?.toFixed(1)) + 'pp';
  return (
    <div className={className}>
      <div className="triangle" />
      {isValueDisplayed && displayValue}
    </div>
  );
};

export const DeltaAtom = styled(Delta)`
  --triangle-half-width: 7px;
  --triangle-height: 12.1px;

  display: inline-flex;
  align-items: baseline;
  ${vars.fontSize4}
  .triangle {
    display: none;
    margin-right: 2px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 var(--triangle-half-width) var(--triangle-height)
      var(--triangle-half-width);
    border-color: transparent transparent ${vars.deltaGreen} transparent;
  }
  ${({ deltaValue }) => {
    if (deltaValue) {
      if (deltaValue > 0) {
        return `color: ${vars.deltaGreen}; .triangle {display: inline-block;}`;
      } else if (deltaValue < 0) {
        return `color: ${vars.deltaRed}; .triangle {display: inline-block; position: relative; top: 1px; border-width: var(--triangle-height) var(--triangle-half-width) 0 var(--triangle-half-width); border-color: ${vars.deltaRed} transparent transparent transparent}`;
      }
    } else {
      return null;
    }
  }}
`;
